import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import './css/Introduction.css';

const featuredKeys = ['terra', 'terra2', 'cosmoshub', 'osmosis', 'akash'];
const formatUsd = (value) => Number(value).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: value < 1 ? 8 : 2,
});

function Introduction() {
  const navigate = useNavigate();
  const [chains, setChains] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState('');
  const [highlights, setHighlights] = useState(null);
  const [highlightsError, setHighlightsError] = useState('');

  useEffect(() => {
    let active = true;
    axios.get('/api/cosmos/chains').then(({ data }) => {
      if (!active) return;
      setChains(data?.chains || []);
    }).catch(() => {
      if (active) setError('The chain list is temporarily unavailable. Please try again shortly.');
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    const loadHighlights = async () => {
      let delay = 5 * 60 * 1000;
      try {
        const { data } = await axios.get('/api/cosmos/home-highlights');
        if (!active) return;
        setHighlights(data);
        setHighlightsError('');
        if (data.upgradesLoading) delay = 10000;
      } catch {
        if (!active) return;
        setHighlightsError('Live highlights are temporarily unavailable.');
        delay = 60000;
      }
      if (active) timer = setTimeout(loadHighlights, delay);
    };
    loadHighlights();
    return () => { active = false; clearTimeout(timer); };
  }, []);

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return chains.filter((chain) => !query || `${chain.prettyName} ${chain.symbol} ${chain.chainId}`.toLowerCase().includes(query));
  }, [chains, search]);
  const featured = featuredKeys.map((key) => chains.find((chain) => chain.key === key)).filter(Boolean);

  return (
    <main className="network-intro">
      <section className="network-intro-hero">
        <div className="network-intro-copy">
          <p className="network-intro-eyebrow">Cosmos-Station · The Cosmos ecosystem</p>
          <h1>One place to explore the chains behind Cosmos.</h1>
          <p className="network-intro-lede">Choose a network to see its own live blocks, staking, governance, validators and market data. Each dashboard shows the figures available for that chain.</p>
          <div className="network-intro-actions">
            <Link to="/stats" className="network-intro-primary">Explore networks <span aria-hidden="true">↗</span></Link>
            <Link to="/Services" className="network-intro-secondary">Browse validators</Link>
          </div>
        </div>
        <div className="network-intro-art" aria-hidden="true">
          <div className="network-intro-orbit orbit-one"><span /></div>
          <div className="network-intro-orbit orbit-two"><span /></div>
          <div className="network-intro-orbit orbit-three"><span /></div>
          <div className="network-intro-logo">
            <span className="network-intro-logo-moon" />
            <span className="network-intro-logo-letters"><span>C</span>S</span>
            <span className="network-intro-logo-caption">Cosmos<br />Station</span>
          </div>
        </div>
      </section>

      <section className="network-intro-explorer" aria-labelledby="explore-heading">
        <div className="network-intro-section-heading">
          <div><p>Explore networks</p><h2 id="explore-heading">Find your chain</h2></div>
          {chains.length > 0 && <span>{chains.length} listed networks</span>}
        </div>
        <div className="network-intro-picker">
          <label htmlFor="intro-chain-search">Search by name, ticker or chain ID
            <input id="intro-chain-search" type="search" value={search} onChange={(event) => { setSearch(event.target.value); setSelection(''); }} placeholder="Search networks" />
          </label>
          <label htmlFor="intro-chain-select">Select a chain
            <select id="intro-chain-select" value={matches.some((chain) => chain.key === selection) ? selection : ''} onChange={(event) => setSelection(event.target.value)}>
              <option value="">Choose a network</option>
              {matches.map((chain) => <option key={chain.key} value={chain.key}>{chain.prettyName} ({chain.symbol})</option>)}
            </select>
          </label>
          <button type="button" onClick={() => selection && navigate(`/stats/${selection}`)} disabled={!selection}>View dashboard <span aria-hidden="true">→</span></button>
        </div>
        {error && <p className="network-intro-error" role="alert">{error}</p>}
        {!error && chains.length === 0 && <p className="network-intro-hint">Loading networks…</p>}
        {chains.length > 0 && matches.length === 0 && <p className="network-intro-hint">No listed network matches your search.</p>}
      </section>

      {featured.length > 0 && <section className="network-intro-featured" aria-labelledby="featured-heading">
        <div className="network-intro-section-heading"><div><p>Start here</p><h2 id="featured-heading">Popular networks</h2></div><Link to="/stats">All chain stats →</Link></div>
        <div className="network-intro-featured-grid">
          {featured.map((chain) => <Link to={`/stats/${chain.key}`} key={chain.key} className="network-intro-chain-card">
            <span className="network-intro-chain-logo">{chain.logo ? <img src={chain.logo} alt="" loading="lazy" /> : chain.symbol.slice(0, 2)}</span>
            <span className="network-intro-chain-name">{chain.prettyName}</span>
            <span className="network-intro-chain-meta">{chain.symbol} · {chain.chainId}</span>
            <span className="network-intro-chain-arrow" aria-hidden="true">↗</span>
          </Link>)}
        </div>
      </section>}

      <section className="network-intro-highlights" aria-labelledby="performers-heading">
        <div className="network-intro-section-heading"><div><p>Market leaders</p><h2 id="performers-heading">Best performing chains</h2></div><span>24h USD price change</span></div>
        <p className="network-intro-section-note">Ranked by 24-hour price change among listed chains with at least $1m market cap and $100k trading volume.</p>
        {highlightsError && <p className="network-intro-error" role="status">{highlightsError}</p>}
        {!highlights && !highlightsError && <p className="network-intro-hint">Loading market performance…</p>}
        {highlights && highlights.bestPerformers.length === 0 && <p className="network-intro-empty">No verified market performance is available right now.</p>}
        {highlights && highlights.bestPerformers.length > 0 && <div className="network-intro-featured-grid">
          {highlights.bestPerformers.map((chain, index) => <Link to={`/stats/${chain.key}`} key={chain.key} className="network-intro-chain-card network-intro-performance-card">
            <span className="network-intro-card-rank">#{index + 1}</span>
            <span className="network-intro-chain-logo">{chain.logo ? <img src={chain.logo} alt="" loading="lazy" /> : chain.symbol.slice(0, 2)}</span>
            <span className="network-intro-chain-name">{chain.prettyName}</span>
            <span className="network-intro-chain-meta">{chain.symbol} · {formatUsd(chain.priceUsd)}</span>
            <strong className={chain.change24h >= 0 ? 'network-intro-gain' : 'network-intro-loss'}>{chain.change24h >= 0 ? '+' : ''}{chain.change24h.toFixed(2)}% <small>24h</small></strong>
          </Link>)}
        </div>}
      </section>

      <section className="network-intro-highlights" aria-labelledby="upgrades-heading">
        <div className="network-intro-section-heading"><div><p>Network roadmap</p><h2 id="upgrades-heading">Chains with upgrades coming</h2></div>{highlights?.upgradesCheckedAt && <span>Checked {new Date(highlights.upgradesCheckedAt).toLocaleString('en-GB')}</span>}</div>
        <p className="network-intro-section-note">Scheduled plans reported on-chain. Upgrade heights are shown because a calendar date cannot be verified from block height alone.</p>
        {!highlights && !highlightsError && <p className="network-intro-hint">Checking for scheduled network upgrades…</p>}
        {highlights?.upgradesLoading && highlights.upgrades.length === 0 && <p className="network-intro-hint">Checking the listed chains for scheduled upgrades…</p>}
        {highlights && !highlights.upgradesLoading && highlights.upgrades.length === 0 && <p className="network-intro-empty">No scheduled on-chain upgrades were reported by the available chain endpoints.</p>}
        {highlights?.upgrades.length > 0 && <div className="network-intro-featured-grid">
          {highlights.upgrades.map((chain) => {
            const content = <>
            <span className="network-intro-chain-logo">{chain.logo ? <img src={chain.logo} alt="" loading="lazy" /> : chain.symbol.slice(0, 2)}</span>
            <span className="network-intro-chain-name">{chain.prettyName}</span>
            <span className="network-intro-chain-meta">{chain.chainId}</span>
            <strong>{chain.planName}</strong>
            <span className="network-intro-upgrade-height">Scheduled at block {chain.height.toLocaleString('en-GB')}</span>
            {chain.proposalId && <span className="network-intro-upgrade-proposal">View proposal #{chain.proposalId} →</span>}
            </>;
            return chain.proposalId
              ? <Link to={`/stats/${chain.key}?proposal=${encodeURIComponent(chain.proposalId)}#cosmos-proposal-explorer`} key={chain.key} className="network-intro-chain-card network-intro-upgrade-card">{content}</Link>
              : <article key={chain.key} className="network-intro-chain-card network-intro-upgrade-card network-intro-upgrade-unlinked">{content}</article>;
          })}
        </div>}
      </section>

      <section className="network-intro-footer-band">
        <div><p>Terra Classic</p><h2>The original LUNC dashboard is still here.</h2><span>Explore LUNC burns, USTC, taxes, pools, proposals and network health in detail.</span></div>
        <Link to="/classic-dashboard">Open detailed LUNC dashboard →</Link>
      </section>
    </main>
  );
}

export default Introduction;
